import { useContext, useState } from "react";
import ThemeContext from "../../lib/theme-context";
import EventBadge from "./EventBadge";

const CalendarDayCell = ({
  day,
  events,
  isToday,
  isCurrentMonth,
  onEventClick,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragOver,
  viewMode = "month",
}) => {
  const theme = useContext(ThemeContext);
  const isDark = theme[0] === "dark";
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const isWeekView = viewMode === "week";

  const getCellBackgroundColor = () => {
    if (!isCurrentMonth) {
      return isDark ? "#0f1012" : "#f3f4f6";
    }

    if (isDragOver) {
      return isDark ? "rgba(55, 136, 216, 0.15)" : "rgba(55, 136, 216, 0.1)";
    }

    if (isHovered && events.length > 0) {
      return isDark ? "#1e1f23" : "#f9fafb";
    }

    return isDark ? "#1a1b1e" : "#ffffff";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (onDragOver) onDragOver(day);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (onDrop) onDrop(day);
  };

  const handleDragLeave = () => {
    if (onDragLeave) onDragLeave();
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const maxVisibleEvents = isWeekView ? 20 : (isMobile ? 2 : 4);
  const visibleEvents = isExpanded ? events : events.slice(0, maxVisibleEvents);
  const remainingCount = events.length - maxVisibleEvents;

  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: getCellBackgroundColor(),
        border: isToday
          ? `clamp(1.5px, 0.4vw, 2px) solid #3788d8`
          : `1px solid ${isDark ? "#2d2e33" : "#e5e7eb"}`,
        borderRadius: "clamp(4px, 1vw, 8px)",
        minHeight: isExpanded ? "auto" : isWeekView ? "clamp(200px, 50vh, 400px)" : "clamp(80px, 20vw, 140px)",
        maxHeight: isExpanded ? "calc(100vh - 200px)" : isWeekView ? "calc(100vh - 200px)" : "none",
        padding: "clamp(4px, 1vw, 8px)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        opacity: isCurrentMonth ? 1 : 0.4,
        boxShadow: isHovered && events.length > 0
          ? isDark
            ? "0 2px 8px rgba(0, 0, 0, 0.3)"
            : "0 2px 8px rgba(0, 0, 0, 0.08)"
          : "none",
        zIndex: isExpanded ? 10 : 1,
      }}
    >
      <div
        style={{
          fontSize: "clamp(11px, 2vw, 14px)",
          fontWeight: isToday ? "700" : "600",
          color: isToday
            ? "#3788d8"
            : isDark
              ? "#e5e7eb"
              : "#374151",
          marginBottom: "clamp(4px, 1vw, 8px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{day.format("D")}</span>
        {events.length > 0 && isMobile && (
          <span
            style={{
              fontSize: "clamp(8px, 1.5vw, 9px)",
              fontWeight: "600",
              color: isDark ? "#6b7280" : "#9ca3af",
              backgroundColor: isDark ? "#2d2e33" : "#f3f4f6",
              padding: "clamp(1px, 0.3vw, 2px) clamp(4px, 1vw, 6px)",
              borderRadius: "10px",
            }}
          >
            {events.length}
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "3px",
          flex: 1,
          overflow: isExpanded ? "auto" : "hidden",
        }}
      >
        {visibleEvents.map((event, index) => {
          const isDraggable =
            (event.category === "forecast" ||
            event.category === "reflection") &&
            event.category !== "holiday";

          return (
            <EventBadge
              key={`${event.publicId}-${index}`}
              event={event}
              onClick={onEventClick}
              isDraggable={isDraggable}
              onDragStart={onDragStart}
            />
          );
        })}

        {remainingCount > 0 && !isExpanded && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
            style={{
              fontSize: "clamp(8px, 1.5vw, 10px)",
              fontWeight: "600",
              color: "#3788d8",
              padding: "clamp(2px, 0.5vw, 4px) clamp(4px, 1vw, 8px)",
              marginTop: "2px",
              backgroundColor: isDark
                ? "rgba(55, 136, 216, 0.15)"
                : "rgba(55, 136, 216, 0.1)",
              border: `1px solid #3788d8`,
              borderRadius: "clamp(3px, 0.5vw, 4px)",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark
                ? "rgba(55, 136, 216, 0.25)"
                : "rgba(55, 136, 216, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark
                ? "rgba(55, 136, 216, 0.15)"
                : "rgba(55, 136, 216, 0.1)";
            }}
          >
            +{remainingCount} more
          </div>
        )}

        {isExpanded && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(false);
            }}
            style={{
              fontSize: "clamp(8px, 1.5vw, 10px)",
              fontWeight: "600",
              color: isDark ? "#9ca3af" : "#6b7280",
              padding: "clamp(2px, 0.5vw, 4px) clamp(4px, 1vw, 8px)",
              marginTop: "2px",
              backgroundColor: isDark ? "#2d2e33" : "#f3f4f6",
              borderRadius: "clamp(3px, 0.5vw, 4px)",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#3d3e43" : "#e5e7eb";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#2d2e33" : "#f3f4f6";
            }}
          >
            Show less
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarDayCell;
